import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import {
  IoTimeOutline,
  IoCubeOutline,
  IoBarcodeOutline,
  IoCheckmarkCircleOutline,
  IoAlertCircleOutline,
  IoRefreshOutline,
  IoArrowForwardOutline,
  IoPrintOutline,
  IoDocumentTextOutline,
  IoCheckmarkCircle,
  IoListOutline,
  IoCarOutline,
  IoCarSportOutline,
} from 'react-icons/io5';
import { api, type FbsPostingItem, type ShipmentRow, type FbsActListItem } from '../api';

const STATUSES = [
  { value: 'awaiting_packaging', label: 'Ожидают сборки' },
  { value: 'awaiting_deliver', label: 'Готовы к отгрузке' },
  { value: 'delivering', label: 'В доставке' },
  { value: 'delivered', label: 'Доставлены' },
  { value: 'cancelled', label: 'Отменены' },
] as const;

export function ShipmentsPage() {
  const [status, setStatus] = useState<(typeof STATUSES)[number]['value']>('awaiting_packaging');
  const postings = useQuery({
    queryKey: ['fbs-postings', status],
    queryFn: () => api.fbsPostings(status),
    refetchInterval: 30_000,
  });

  return (
    <>
      <div className="page-header">
        <div>
          <h2>Отгрузки</h2>
          <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>FBS-отправления Ozon · автообновление каждые 30 сек</div>
        </div>
      </div>

      <DayWorkflow />

      {/* Status tabs */}
      <div className="status-tabs">
        {STATUSES.map((s) => (
          <button
            key={s.value}
            className={`status-tab${status === s.value ? ' active' : ''}`}
            onClick={() => setStatus(s.value)}
          >
            {s.label}
          </button>
        ))}
      </div>

      {postings.isLoading && <div className="card">Загрузка…</div>}
      {postings.error && <div className="card" style={{ color: 'var(--red)' }}>{(postings.error as Error).message}</div>}

      {postings.data && (
        <>
          <div className="muted" style={{ marginBottom: 12, fontSize: 13 }}>
            Найдено: <strong style={{ color: 'var(--text)' }}>{postings.data.total}</strong>
          </div>
          {postings.data.items.length === 0 ? (
            <div className="card empty">Нет отправлений с этим статусом.</div>
          ) : (
            <div className="postings-grid">
              {postings.data.items.map((p) => <PostingCard key={p.posting_number} p={p} statusKey={status} />)}
            </div>
          )}
        </>
      )}

      <HistoryBlock />
    </>
  );
}

// ──────────────────────────────────────────────────────────────
// Daily workflow widget: 4-step visual progress
// ──────────────────────────────────────────────────────────────
function DayWorkflow() {
  const awaitingPacking = useQuery({
    queryKey: ['fbs-postings', 'awaiting_packaging'],
    queryFn: () => api.fbsPostings('awaiting_packaging'),
    refetchInterval: 30_000,
  });
  const awaitingDeliver = useQuery({
    queryKey: ['fbs-postings', 'awaiting_deliver'],
    queryFn: () => api.fbsPostings('awaiting_deliver'),
    refetchInterval: 30_000,
  });
  const acts = useQuery({ queryKey: ['fbs-acts'], queryFn: api.fbsActsList, refetchInterval: 30_000 });

  // Локально храним: распечатали ли мы лист сборки сегодня, печатали ли этикетки
  const [pickingPrinted, setPickingPrinted] = useState<boolean>(() => readDailyFlag('pickingPrinted'));
  const [labelsPrinted, setLabelsPrinted] = useState<boolean>(() => readDailyFlag('labelsPrinted'));
  useEffect(() => writeDailyFlag('pickingPrinted', pickingPrinted), [pickingPrinted]);
  useEffect(() => writeDailyFlag('labelsPrinted', labelsPrinted), [labelsPrinted]);

  const packCount = awaitingPacking.data?.total ?? 0;
  const deliverCount = awaitingDeliver.data?.total ?? 0;
  const todayActs = useMemo(() => (acts.data?.items ?? []).filter((a) => isToday(a.created_at) || isToday(a.departure_date)), [acts.data]);
  const activeAct = todayActs.find((a) => a.status !== 'fail' && a.status !== 'cancelled');

  // Логика статусов шагов
  const step1Done = pickingPrinted || packCount === 0;
  const step2Done = packCount === 0;
  const step3Done = (labelsPrinted || deliverCount === 0) && step2Done;
  const step4Done = activeAct && ['ready', 'formed', 'confirmed', 'closed', 'document_not_ready', 'no_act_required'].includes(activeAct.status);

  // Активный шаг: первый незавершённый
  const activeStep: 1 | 2 | 3 | 4 =
    !step1Done ? 1 :
    !step2Done ? 2 :
    !step3Done ? 3 :
    !step4Done ? 4 : 4;

  return (
    <div className="day-workflow">
      <div className="day-workflow-header">
        <h3 style={{ margin: 0, fontSize: 16 }}>
          Сегодняшняя сборка
          <span className="muted" style={{ fontSize: 13, fontWeight: 400, marginLeft: 12 }}>
            {new Date().toLocaleDateString('ru-RU', { weekday: 'long', day: 'numeric', month: 'long' })}
          </span>
        </h3>
        {(step1Done && step2Done && step3Done && step4Done) && (
          <span className="badge badge-success" style={{ fontSize: 12 }}>
            <IoCheckmarkCircle /> Все шаги завершены
          </span>
        )}
      </div>

      <div className="day-workflow-steps">
        <WorkflowStep
          num={1}
          active={activeStep === 1}
          done={step1Done}
          icon={<IoListOutline />}
          title="Список сборки"
          subtitle={packCount > 0 ? `${packCount} отправлений к сборке` : 'нечего собирать'}
          action={
            packCount > 0 ? (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <Link
                  to="/shipments/picking-list"
                  className="btn primary"
                  onClick={() => setPickingPrinted(true)}
                >
                  <IoPrintOutline /> Печать списка
                </Link>
                {pickingPrinted && (
                  <button className="btn" onClick={() => setPickingPrinted(false)} title="Сбросить отметку">
                    Сброс
                  </button>
                )}
              </div>
            ) : null
          }
        />

        <WorkflowStep
          num={2}
          active={activeStep === 2}
          done={step2Done}
          icon={<IoBarcodeOutline />}
          title="Сборка и маркировка"
          subtitle={packCount > 0 ? `${packCount} к обработке` : 'все собраны'}
          action={packCount > 0 ? <ScrollToPostingsBtn /> : null}
        />

        <WorkflowStep
          num={3}
          active={activeStep === 3}
          done={step3Done}
          icon={<IoDocumentTextOutline />}
          title="Этикетки"
          subtitle={deliverCount > 0 ? `${deliverCount} готовы к печати` : 'нет готовых'}
          action={
            deliverCount > 0 ? (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <a
                  href={api.fbsBulkLabelsUrl('awaiting_deliver')}
                  target="_blank"
                  rel="noreferrer"
                  className="btn primary"
                  onClick={() => setLabelsPrinted(true)}
                >
                  <IoPrintOutline /> Все этикетки PDF
                </a>
                {labelsPrinted && (
                  <button className="btn" onClick={() => setLabelsPrinted(false)} title="Сбросить отметку">
                    Сброс
                  </button>
                )}
              </div>
            ) : null
          }
        />

        <WorkflowStep
          num={4}
          active={activeStep === 4}
          done={!!step4Done}
          icon={<IoCarSportOutline />}
          title="Передача курьеру"
          subtitle={activeAct ? `Документ #${activeAct.id} — ${actStatusLabel(activeAct.status)}` : (deliverCount > 0 ? 'нужно запросить документ передачи' : 'нет отправлений')}
          action={<ActButton act={activeAct} deliverCount={deliverCount} postings={awaitingDeliver.data?.items ?? []} />}
        />
      </div>
    </div>
  );
}

function WorkflowStep({ num, active, done, icon, title, subtitle, action }: {
  num: number;
  active: boolean;
  done: boolean;
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  action: React.ReactNode;
}) {
  return (
    <div className={`workflow-step ${done ? 'done' : active ? 'active' : 'pending'}`}>
      <div className="workflow-step-num">
        {done ? <IoCheckmarkCircle /> : num}
      </div>
      <div className="workflow-step-body">
        <div className="workflow-step-title">
          <span className="workflow-step-icon">{icon}</span>
          {title}
        </div>
        <div className="workflow-step-subtitle">{subtitle}</div>
        {action && <div className="workflow-step-action">{action}</div>}
      </div>
    </div>
  );
}

function ScrollToPostingsBtn() {
  return (
    <button
      className="btn primary"
      onClick={() => {
        const el = document.querySelector('.status-tabs');
        el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }}
    >
      Перейти к списку <IoArrowForwardOutline />
    </button>
  );
}

function ActButton({ act, deliverCount, postings }: { act: FbsActListItem | undefined; deliverCount: number; postings: FbsPostingItem[] }) {
  const qc = useQueryClient();

  // Извлекаем delivery_method_id из доступных постингов
  const deliveryMethodId = useMemo(() => {
    // Все постинги обычно одного метода доставки. Берём из первого.
    // postings объект из /api/fbs/postings — там нет delivery_method id напрямую, только name
    return null;
  }, [postings]);

  const create = useMutation({
    mutationFn: () => api.fbsActCreate({
      containers_count: deliverCount,
      delivery_method_id: deliveryMethodId ?? 0, // Ozon обычно сам определяет если все одного метода
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fbs-acts'] }),
  });

  if (act) {
    return (
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {['ready', 'formed', 'confirmed', 'closed', 'document_not_ready'].includes(act.status) && (
          <a href={api.fbsActPdfUrl(act.id)} target="_blank" rel="noreferrer" className="btn primary">
            <IoDocumentTextOutline /> Документ ТН/акт
          </a>
        )}
        {act.status === 'in_process' && (
          <button className="btn" onClick={() => qc.invalidateQueries({ queryKey: ['fbs-acts'] })}>
            <IoRefreshOutline /> Обновить статус
          </button>
        )}
      </div>
    );
  }

  if (deliverCount === 0) return null;

  return (
    <div>
      <button
        className="btn primary"
        onClick={() => {
          if (confirm(`Запросить документ передачи (ТН или акт — зависит от метода доставки) для ${deliverCount} отправлений?`)) {
            create.mutate();
          }
        }}
        disabled={create.isPending}
      >
        <IoCarSportOutline /> {create.isPending ? 'Запрос…' : 'Запросить документ'}
      </button>
      {create.error && (
        <div style={{ color: 'var(--red)', fontSize: 11, marginTop: 4 }}>
          {(create.error as Error).message}
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Posting card (одно отправление в списке)
// ──────────────────────────────────────────────────────────────
function PostingCard({ p, statusKey }: { p: FbsPostingItem; statusKey: string }) {
  const needsMark = p.products.some((pr) => pr.mandatory_mark_required);
  const totalItems = p.products.reduce((s, pr) => s + pr.quantity, 0);
  const deadline = p.shipment_date ? new Date(p.shipment_date) : null;
  const hoursLeft = deadline ? Math.round((deadline.getTime() - Date.now()) / 3600_000) : null;
  const overdue = hoursLeft != null && hoursLeft <= 0;
  const urgent = hoursLeft != null && hoursLeft > 0 && hoursLeft < 4;

  return (
    <Link to={`/shipments/${p.posting_number}`} className="posting-card">
      <div className="posting-card-header">
        <div>
          <div className="posting-number">{p.posting_number}</div>
          <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>{p.delivery_method_name ?? 'без метода доставки'}</div>
        </div>
        <div className="posting-card-badges">
          {needsMark && statusKey === 'awaiting_packaging' && (
            <span className="badge badge-warn">
              <IoBarcodeOutline /> Маркировка
            </span>
          )}
          {hoursLeft != null && statusKey === 'awaiting_packaging' && (
            <span className={`badge ${overdue ? 'badge-error' : urgent ? 'badge-warn' : ''}`}>
              <IoTimeOutline /> {overdue ? 'просрочено' : `${hoursLeft}ч`}
            </span>
          )}
        </div>
      </div>

      <div className="posting-card-meta">
        <span className="posting-card-meta-item">
          <IoCubeOutline /> {totalItems} ед. · {p.products.length} {p.products.length === 1 ? 'позиция' : 'позиций'}
        </span>
      </div>

      <ul className="posting-card-items">
        {p.products.slice(0, 3).map((pr) => (
          <li key={pr.product_id}>
            <span className="posting-card-item-name">{pr.name}</span>
            <span className="posting-card-item-qty">× {pr.quantity}</span>
            {pr.mandatory_mark_required && <IoBarcodeOutline className="posting-card-item-mark" />}
          </li>
        ))}
        {p.products.length > 3 && (
          <li className="muted" style={{ fontSize: 12, fontStyle: 'italic' }}>… и ещё {p.products.length - 3}</li>
        )}
      </ul>

      <div className="posting-card-cta">
        {statusKey === 'awaiting_packaging' ? <>Открыть сборку <IoArrowForwardOutline /></> : <>Подробнее <IoArrowForwardOutline /></>}
      </div>
    </Link>
  );
}

// ──────────────────────────────────────────────────────────────
// История отгрузок
// ──────────────────────────────────────────────────────────────
function HistoryBlock() {
  const qc = useQueryClient();
  const ships = useQuery({ queryKey: ['shipments-history'], queryFn: () => api.fbsShipments(20), refetchInterval: 30_000 });
  const resync = useMutation({
    mutationFn: (id: string) => api.fbsResyncOneC(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shipments-history'] }),
  });

  return (
    <div className="card" style={{ marginTop: 32 }}>
      <div style={{ padding: '20px 20px 0' }}>
        <h3 style={{ margin: 0 }}>Последние отгрузки</h3>
        <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>Что уже отправили в Ozon — с статусом синхронизации 1С</div>
      </div>
      <table className="products" style={{ marginTop: 16 }}>
        <thead>
          <tr>
            <th>Номер отправления</th>
            <th>Когда</th>
            <th>Доставка</th>
            <th className="num">Марок</th>
            <th>1С</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {ships.data?.items.length === 0 && (
            <tr><td colSpan={6} className="empty">Истории пока нет — пока не было ни одной отгрузки.</td></tr>
          )}
          {ships.data?.items.map((s) => (
            <ShipmentHistoryRow key={s.id} s={s} onResync={() => resync.mutate(s.id)} resyncing={resync.isPending} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ShipmentHistoryRow({ s, onResync, resyncing }: { s: ShipmentRow; onResync: () => void; resyncing: boolean }) {
  return (
    <tr>
      <td><code style={{ fontSize: 12 }}>{s.posting_number}</code></td>
      <td className="muted" style={{ fontSize: 12 }}>{s.shipped_at ? new Date(s.shipped_at).toLocaleString('ru-RU') : '—'}</td>
      <td className="muted" style={{ fontSize: 12 }}>{s.delivery_method ?? '—'}</td>
      <td className="num">{s.marks_count}</td>
      <td>
        {s.onec_synced_at && (
          <span className="badge badge-success">
            <IoCheckmarkCircleOutline /> {s.onec_document_id ?? 'ok'}
          </span>
        )}
        {s.onec_error && (
          <span className="badge badge-error" title={s.onec_error}>
            <IoAlertCircleOutline /> ошибка
          </span>
        )}
        {!s.onec_synced_at && !s.onec_error && <span className="muted" style={{ fontSize: 12 }}>—</span>}
      </td>
      <td>
        {(s.onec_error || !s.onec_synced_at) && (
          <button className="btn" onClick={onResync} disabled={resyncing}>
            <IoRefreshOutline /> {resyncing ? 'Синк…' : 'Повторить 1С'}
          </button>
        )}
      </td>
    </tr>
  );
}

// ──────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────
function readDailyFlag(key: string): boolean {
  try {
    const raw = localStorage.getItem(`ozr.day.${key}`);
    if (!raw) return false;
    const data = JSON.parse(raw) as { date: string; value: boolean };
    return data.date === todayKey() && data.value;
  } catch { return false; }
}

function writeDailyFlag(key: string, value: boolean): void {
  try {
    localStorage.setItem(`ozr.day.${key}`, JSON.stringify({ date: todayKey(), value }));
  } catch {/* ignore */}
}

function todayKey(): string {
  const d = new Date();
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

function isToday(iso?: string): boolean {
  if (!iso) return false;
  const d = new Date(iso);
  const t = new Date();
  return d.getFullYear() === t.getFullYear() && d.getMonth() === t.getMonth() && d.getDate() === t.getDate();
}

function actStatusLabel(status: string): string {
  const map: Record<string, string> = {
    in_process: 'формируется',
    ready: 'готов',
    formed: 'сформирован',
    confirmed: 'подтверждён',
    closed: 'закрыт',
    no_act_required: 'не требуется',
    document_not_ready: 'документ не готов',
    fail: 'ошибка',
    cancelled: 'отменён',
  };
  return map[status] ?? status;
}
